import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { WS_API, BLOCK_INFOS_API, BLOCK_CANDLES_API, isNumber, MERGE_BLOCK } from "../utils/constants";
import BlockNextUI from '../ui/block_next.js';
import TxPoolUI from '../ui/tx_pool.js';
import SlotProgressUI from '../ui/slot_progress.js';
import BlockValueUI from '../ui/block_value.js';
import BlockBidUI from '../ui/block_bid.js';
import BlockTableUI from '../ui/block_table.js';
import BlockHistoryUI from '../ui/block_history.js';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton } from '@mui/material';

function DataLiveView({ onBlockClick }) {
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState("");
    const [filterValue, setFilterValue] = useState("");
    const [error, setError] = useState("");
    const wsRef = useRef(null);

    const uiNextBlockRef = useRef(null);
    const uiTxPoolRef = useRef(null);
    const uiSlotProgressRef = useRef(null);
    const uiBlockValueRef = useRef(null);
    const uiBlockBidRef = useRef(null);
    const uiBlockTableRef = useRef(null);
    const uiBlockHistoryRef = useRef(null);

    useEffect(() => {
        uiNextBlockRef.current = new BlockNextUI("#block_next");
        uiTxPoolRef.current = new TxPoolUI("#tx_pool");
        uiSlotProgressRef.current = new SlotProgressUI("#slot_progress_vl");
        uiBlockValueRef.current = new BlockValueUI("#block_value_vl");
        uiBlockBidRef.current = new BlockBidUI("#block_bid_vl");

        startWebSocket();

        return () => {
            stopWebSocket();
        };
    }, []);

    const setupHistory = () => {
        const fetchBlockInfos = fetch(BLOCK_INFOS_API)
            .then(response => response.json())
            .catch(error => {
                console.error('Unable to retrieve latest block infos', error);
                throw error;
            });

        const fetchBlockCandles = fetch(BLOCK_CANDLES_API)
            .then(response => response.json())
            .catch(error => {
                console.error('Unable to retrieve latest block candles', error);
                throw error;
            });

        Promise.all([fetchBlockInfos, fetchBlockCandles])
            .then(([blockInfosData, blockCandlesData]) => {
                uiBlockTableRef.current = new BlockTableUI("#block_table", blockInfosData, onBlockClick);
                uiBlockHistoryRef.current = new BlockHistoryUI("#block_history_candles", "#block_history_blocks", uiBlockTableRef.current);

                uiBlockHistoryRef.current.setBlockInfos(blockInfosData);

                if (blockCandlesData.length > 168) {
                    uiBlockHistoryRef.current.setBlockCandles(blockCandlesData.slice(-168));
                } else {
                    uiBlockHistoryRef.current.setBlockCandles(blockCandlesData);
                }
            })
            .catch(error => {
                console.error('Unable to retrieve latest block data', error);
            });
    };

    const startWebSocket = () => {
        if (uiBlockHistoryRef.current && uiBlockTableRef.current) {
            fetch(BLOCK_INFOS_API)
                .then(response => response.json())
                .then(data => {
                    uiBlockHistoryRef.current?.setBlockInfos(data);
                    uiBlockTableRef.current?.setBlockInfos(data);
                })
                .catch(error => {
                    console.error('Unable to retrieve latest block infos', error);
                });
        }

        wsRef.current = new WebSocket(WS_API);
        wsRef.current.onmessage = (evt) => {
            update(evt.data);
        };
        uiBlockBidRef.current?.start();
    };

    const stopWebSocket = () => {
        if (wsRef.current) {
            wsRef.current.close();
        }
        uiBlockBidRef.current?.pause();
    };

    const handleSearch = () => {
        if (!searchValue) return;

        if (!isNumber(searchValue) || searchValue < MERGE_BLOCK) {
            let str = "";
            if (!isNumber(searchValue)) {
                str = `'${searchValue}' is not a valid block number.`;
            } else if (searchValue < MERGE_BLOCK) {
                str = `Blocks before merge (<${MERGE_BLOCK}) not supported.`;
            }
            setError(str);
            setTimeout(() => setError(""), 3000);
            return;
        }

        setError("");
        navigate(`/data/${searchValue}`);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const handleResetFilter = () => {
        setFilterValue("");
        const filterInput = document.getElementById('filter-input-block-table');
        if (filterInput) {
            filterInput.value = "";
        }
    };

    const update = (msg) => {
        var obj = JSON.parse(msg);
        if (obj.type == "bid_summary") {
            uiBlockBidRef.current?.newBidSummary(obj.data);
            uiBlockValueRef.current?.newBidSummary(obj.data);
            uiNextBlockRef.current?.newBidSummary(obj.data);
        } else if (obj.type == "pool_summary") {
            uiTxPoolRef.current?.newPoolSummary(obj.data);
            uiSlotProgressRef.current?.newPoolSummary(obj.data);
        } else if (obj.type == "block_summary") {
            uiBlockValueRef.current?.newBlockSummary(obj.data);
            uiNextBlockRef.current?.newBlockSummary(obj.data);
            uiBlockTableRef.current?.newBlockSummary(obj.data);
            uiSlotProgressRef.current?.newBlockSummary(obj.data);
            uiBlockBidRef.current?.reset();
        } else if (obj.type == "block_info") {
            uiBlockHistoryRef.current?.newBlockInfo(obj.data);
            uiBlockTableRef.current?.newBlockInfo(obj.data);
        }
    };

    useEffect(() => {
        setupHistory();
    }, []);

    return (
        <div className="responsive-grid">
            <div className="left-section">
                <div className="tooltip-wrapper">
                    <div className="tooltip tooltip-centered">
                        <div className="section-heading graph-heading">Transaction Pool</div>
                        <span className="tooltiptext">
                            Live view of all pending transactions in the Ethereum transaction pool. The y value represents the maximum base fee set for a transaction, while the x value represents the priority fee.
                            <br /><br />
                            <strong>Lines:</strong>
                            <ul>
                                <li><strong>Horizontal:</strong> Represents the base fee for the next block.</li>
                                <li><strong>Vertical:</strong> Indicates the approximate priority fee for transactions to be included in the next block.</li>
                            </ul>
                            <strong>Transactions:</strong>
                            <ul>
                                <li><strong style={{ color: '#26a6ed' }}>Blue:</strong> ETH transfer.</li>
                                <li><strong style={{ color: '#00A36C' }}>Green:</strong> Token interactions, such as transfers or approvals.</li>
                                <li><strong style={{ color: '#ff697c' }}>Red:</strong> Transactions to or from a CEX.</li>
                                <li><strong style={{ color: '#4b2caf' }}>Navy:</strong> Swaps and liquidity actions on a DEX.</li>
                                <li><strong style={{ color: '#e59c23' }}>Orange:</strong> Contract interactions with DeFi protocols.</li>
                                <li><strong style={{ color: '#ffff00' }}>Yellow:</strong> MEV transactions.</li>
                                <li><strong style={{ color: '#E5E4E2' }}>Grey:</strong> Other transactions.</li>
                            </ul>
                            Once a new block is written, included transactions will be <strong style={{ color: '#5caf56' }}>highlighted</strong> and removed from the graph.
                        </span>
                    </div>
                </div>
                <div className="graph-container" id="tx_pool"></div>

                <div className="graph-row">
                    <div className="graph-column">
                        <div className="tooltip-wrapper">
                            <div className="tooltip tooltip-centered">
                                <div className="section-heading graph-heading centered">Value Over Time</div>
                                <span className="tooltiptext" style={{ width: '400px' }}>
                                    Temporal course of the highest bid submitted across all builders for the next slot.
                                    <br /><br />
                                    <strong>Lines:</strong>
                                    <ul>
                                        <li><strong>Horizontal <span style={{ color: '#26a6ed' }}>Blue:</span></strong> Winning builder submission.</li>
                                        <li><strong>Horizontal <span style={{ color: '#f9f871' }}>Yellow:</span></strong> Best submission for the slot.</li>
                                        <li><strong>Horizontal <span style={{ color: '#fb717f' }}>Red:</span></strong> Vanilla/Private block where no matching bid got found.</li>
                                    </ul>
                                </span>
                            </div>
                        </div>
                        <div className="graph-container" id="block_value_vl"></div>
                    </div>

                    <div className="graph-column">
                        <div className="tooltip-wrapper">
                            <div className="tooltip tooltip-centered">
                                <div className="section-heading graph-heading centered">Builder Bids</div>
                                <span className="tooltiptext" style={{ width: '400px' }}>
                                    Highest bid submitted by each builder in real-time. Bids are sorted from highest to lowest value. Gas usage is shown in the respective small green bar. The total bid count per builder is depicted on the left.
                                </span>
                            </div>
                        </div>
                        <div className="graph-container" id="block_bid_vl"></div>
                    </div>
                </div>
                <div className="progress-bar-container">
                    <div className="progress-bar" id="slot_progress_vl"></div>
                </div>
                <div id="block_next"></div>
            </div>

            <div className="right-section">
                <div className="tooltip" style={{ alignSelf: 'flex-start' }}>
                    <div className="section-heading graph-heading">Blocks</div>
                    <span className="tooltiptext">
                        <strong>Upper Graph</strong> - Aggregated view of blocks in 1 hour periods.
                        <br /><br />
                        <strong>Bars:</strong>
                        <ul>
                            <li><strong><span style={{ color: '#26a6ed' }}>Blue:</span></strong> Maximum validator payment in the period.</li>
                            <li><strong><span style={{ color: '#00A36C' }}>Green:</span></strong> Maximum builder payout in the period.</li>
                        </ul>
                        <br />
                        <strong>Lower Graph</strong> - Blocks within the last hour.
                        <br /><br />
                        <strong>Bars:</strong>
                        <ul>
                            <li><strong><span style={{ color: '#26a6ed' }}>Blue:</span></strong> Validator payment.</li>
                            <li><strong><span style={{ color: '#e59c23' }}>Orange:</span></strong> Difference between best bid and written block value.</li>
                            <li><strong><span style={{ color: '#00A36C' }}>Green:</span></strong> Builder payout.</li>
                            <li><strong><span style={{ color: '#FFFF00' }}>Yellow:</span></strong> Builder alimony.</li>
                            <li><strong><span style={{ color: '#4b2caf' }}>Navy:</span></strong> Builder payback.</li>
                        </ul>
                        <br />
                        <strong>Fields:</strong>
                        <ul>
                            <li><strong>Payment:</strong> Payment received by the validator of the block.</li>
                            <li><strong>Payout:</strong> Builder payout for the block.</li>
                            <li><strong>Missed:</strong> Potentially missed value for the validator, either by fetching the block too early or building vanilla/private blocks.</li>
                            <li><strong>Offset:</strong> Time between fetching the block by the validator and the end of the slot time.</li>
                        </ul>
                    </span>
                </div>

                <div className="controls-bar">
                    <div className="search-group">
                        <input
                            type="text"
                            id="search-input"
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search #Block..."
                        />
                        <IconButton
                            onClick={handleSearch}
                            sx={{
                                color: '#d3d3d3',
                                '&:hover': { backgroundColor: 'rgba(211, 211, 211, 0.1)' }
                            }}
                        >
                            <SearchIcon />
                        </IconButton>
                    </div>

                    <div className="filter-group">
                        <input
                            type="text"
                            id="filter-input-block-table"
                            value={filterValue}
                            onChange={(e) => setFilterValue(e.target.value)}
                            placeholder="Filter..."
                        />
                        <IconButton
                            onClick={handleResetFilter}
                            sx={{
                                color: '#d3d3d3',
                                '&:hover': { backgroundColor: 'rgba(211, 211, 211, 0.1)' }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </div>

                    {error && <span className="error-text">{error}</span>}
                </div>

                <div className="history-graphs">
                    <div className="history-graph" id="block_history_candles"></div>
                    <div className="history-graph" id="block_history_blocks"></div>
                </div>

                <div className="table-container">
                    <div id="block_table"></div>
                </div>
            </div>
        </div>
    );
}

export default DataLiveView;